import { PimProductInfo } from '../services/products.service';
import { LineType, OfferItem, QuotationQuoteLine, QuoteAssignment } from '../models/quote.model';
import { BomItem } from '../components/questionnaire/configit-quest-adapter/configit-types.interface';
import { ConfigitQuote, Quote, QuoteLine } from '../services/quote.service';
import * as uuid from 'uuid';

export interface HasAssignments {
    assignments: QuoteAssignment[];
}

export interface HasQuantity {
    quantity: number;
}

export abstract class QuoteLineAdapter<L extends HasAssignments, P extends HasQuantity> {
    public abstract getLineByType(quote: Quote, type: LineType): QuotationQuoteLine;

    public abstract getLineByMaterial(quote: Quote, material: string): L;

    public abstract pruneProductItems(materials: string[], quote: Quote): boolean;

    protected abstract getLineByTypeInternal(quote: Quote, type: LineType): L;

    protected abstract addConfigurationLine(
        type: LineType,
        material: string,
        assignments: QuoteAssignment[],
        quote: Quote
    ): void;

    protected abstract addProductItem(
        bomItemId: string,
        quantity: number,
        productInfo: PimProductInfo,
        quote: Quote
    ): void;

    protected abstract getProductItems(quote: Quote, ...materials: string[]): P[];

    public createConfigurationLine(
        material: string,
        type: LineType,
        bomItems: (BomItem & { info: PimProductInfo })[],
        assignments: QuoteAssignment[],
        quote: Quote
    ): L {
        if (!quote) {
            return undefined;
        }

        let line = this.getLineByTypeInternal(quote, type);
        if (line) {
            // merge assignments into line.assignments
            const replaced = line.assignments.map((a1) => ({
                ...a1,
                ...assignments.find((a2) => a2.variableName === a1.variableName),
            }));
            const newAssignments = assignments.filter(
                (a1) => !line.assignments.some((a2) => a2.variableName === a1.variableName)
            );
            this.setLineAssignments([...replaced, ...newAssignments], line);
        } else {
            this.addConfigurationLine(type, material, assignments, quote);
        }
        bomItems.forEach((bomItem) =>
            this.ensureProductItem(bomItem.itemId, bomItem.componentQuantity, bomItem.info, quote)
        );
        return line;
    }

    public setLineAssignments(assignments: QuoteAssignment[], line: HasAssignments) {
        line.assignments = assignments
            .filter((assignment) => !assignment.variableName.startsWith('SapData'))
            .map(({ variableName, valueName, isUserAssignment, isDefault }) => ({
                variableName,
                valueName,
                isUserAssignment,
                isDefault,
            }));
    }

    public ensureProductItem(bomItemId: string, quantity: number, materialInfo: PimProductInfo, quote: Quote): boolean {
        if (quote) {
            const items = this.getProductItems(quote, materialInfo.name);
            let changed: boolean;
            if (items.length) {
                // line for material exists, but quantity may have changed
                const existing = items[0];
                changed = existing.quantity !== quantity;
                existing.quantity = quantity;
            } else {
                // no line for material exists, create one
                this.addProductItem(bomItemId, quantity, materialInfo, quote);
                changed = true;
            }

            return changed;
        }
        return false;
    }
}

export class ConfigitQuoteLineAdapter extends QuoteLineAdapter<QuoteLine, QuoteLine> {
    getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        const line = this.getLineByTypeInternal(quote, type);
        return {
            type,
            material: line.variantCode,
            assignments: line.assignments,
        };
    }

    public getLineByTypeInternal(quote: Quote, type: LineType): QuoteLine {
        const lines = this.getLines(quote.configitQuote, type);
        return lines?.length === 1 ? lines[0] : undefined;
    }

    public getLineByMaterial(quote: Quote, material: string): QuoteLine {
        return quote.configitQuote.lines.find((l) => l.variantCode === material);
    }

    public pruneProductItems(materials: string[], quote: Quote): boolean {
        if (quote) {
            const removed = this.getProductItems(quote, ...materials).filter(
                (line) => !materials.includes(line.variantCode)
            );
            if (removed.length) {
                removed.forEach((line) => {
                    const index = quote.configitQuote.lines.findIndex((l) => l.lineId === line.lineId);
                    quote.configitQuote.lines.splice(index, 1);
                });
                return true;
            }
        }
        return false;
    }

    addProductItem(bomItemId: string, quantity: number, productInfo: PimProductInfo, quote: Quote) {
        const subline = this.createSubLine(quote, bomItemId, quantity, productInfo);
        quote.configitQuote.lines.push(subline);
    }

    getProductItems(quote: Quote, ...materials: string[]) {
        return this.getSubLines(quote.configitQuote, ...materials);
    }

    addConfigurationLine(type: LineType, material: string, assignments: QuoteAssignment[], quote: Quote) {
        const line = this.createConfigitLine(quote, 1, { name: material } as PimProductInfo, type);
        this.setLineAssignments(assignments, line);
        // if there were any lines of that type before, remove them
        quote.configitQuote.lines = quote.configitQuote.lines.filter((l) => this.getLineType(l) !== type);
        quote.configitQuote.lines.push(line);
    }

    private createConfigitLine(
        quote: Quote,
        quantity: number,
        materialInfo: PimProductInfo,
        type?: LineType
    ): QuoteLine {
        const language = quote.configitQuote.language;
        const id = uuid.v4();
        const description = {
            serializedTranslations: [
                {
                    language: materialInfo?.locale || '',
                    translation: materialInfo?.description || '',
                    isDefault: true,
                },
            ],
        };
        return {
            language,
            id,
            quantity,
            properties: [
                {
                    key: 'LineType',
                    value: `"${type}"`,
                },
            ],
            materialVariant: {
                language,
                code: materialInfo.name,
                image: '',
                description,
                assignments: [],
                material: {
                    language,
                    properties: [],
                    name: materialInfo.name,
                    externalId: materialInfo.name,
                    description,
                },
            },
            variantCode: materialInfo.name,
            description,
            lineType: 0,
            createdDate: new Date().toISOString(),
            presetSet: false,
            modelLineType: 0,
            lineId: id,
            assignments: [],
            valid: true,
            bomItemId: '',
        };
    }

    private getSubLines(quote: ConfigitQuote, ...materials: string[]): QuoteLine[] {
        const lines = this.getLines(quote, LineType.sub);
        return materials?.length ? lines.filter((l) => materials.includes(l.variantCode)) : lines;
    }

    private createSubLine(quote: Quote, bomItemId: string, quantity: number, materialInfo: PimProductInfo): QuoteLine {
        const line = this.createConfigitLine(quote, quantity, materialInfo, LineType.sub);
        line.bomItemId = bomItemId;
        line.isSubBomItem = true;
        line.parentId =
            this.getLineByTypeInternal(quote, LineType.secondary)?.lineId ||
            this.getLineByTypeInternal(quote, LineType.primary).lineId;
        return line;
    }

    private getLines(quote: ConfigitQuote, type: LineType) {
        return quote.lines.filter((l) => this.getLineType(l) === type);
    }

    private getLineType(line: QuoteLine): LineType {
        return <LineType>line.properties.find((p) => p.key === 'LineType')?.value?.replace(/"/g, '');
    }
}

export class QuotationQuoteLineAdapter extends QuoteLineAdapter<QuotationQuoteLine, OfferItem> {
    public getLineByType(quote: Quote, type: LineType): QuotationQuoteLine {
        return this.getLineByTypeInternal(quote, type);
    }

    addConfigurationLine(type: LineType, material: string, assignments: QuoteAssignment[], quote: Quote) {
        const line: QuotationQuoteLine = { type, assignments, material };
        const extraData = quote.quotationQuote.offer.extraData;
        // if there were any lines of that type before, remove them
        extraData.lines = extraData.lines.filter((l) => l.type !== type);
        extraData.lines.push(line);
    }

    addProductItem(_: string, quantity: number, productInfo: PimProductInfo, quote: Quote) {
        const items = quote.quotationQuote.offer.items;
        const itemNumber = (items.length ? Math.max(...items.map((i) => i.itemNumber)) : 0) + 1;
        items.push({
            quantity,
            material: productInfo.name,
            optional: false,
            itemNumber,
        });
    }

    getProductItems(quote: Quote, ...materials: string[]) {
        return quote.quotationQuote.offer.items.filter((i) => materials.includes(i.material));
    }

    public getLineByTypeInternal(quote: Quote, type: LineType): QuotationQuoteLine {
        return quote.quotationQuote.offer.extraData.lines.find((l) => l.type === type);
    }

    public getLineByMaterial(quote: Quote, material: string): QuotationQuoteLine {
        return quote.quotationQuote.offer.extraData.lines.find((l) => l.material === material);
    }

    public pruneProductItems(materials: string[], quote: Quote): boolean {
        if (quote) {
            const offer = quote.quotationQuote.offer;
            const removed = offer.items.filter((i) => !materials.includes(i.material));
            if (removed.length) {
                removed.forEach((item) => {
                    const index = offer.items.findIndex((i) => i.itemNumber === item.itemNumber);
                    offer.items.splice(index, 1);
                });

                return true;
            }
        }
        return false;
    }
}
